import type { ReactNode } from 'react';
import { useEffect } from 'react';

import { Suspense } from '@sorare/core/src/atoms/Suspense';
import { useDocumentPictureInPicture } from '@sorare/core/src/hooks/useDocumentPictureInPicture';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';

import { MyBidsPictureInPictureProvider as MyBidsPictureInPictureContextProvider } from './index.ts';
import { MyBidsPictureInPictureWindow } from './lazy.tsx';

/**
 * Owns the picture-in-picture window at app level so it survives navigation —
 * the marketplace banner that opens it is mounted per page and would otherwise
 * take the window down with it. Everything the window renders is loaded only
 * once it is opened, see ./Window.tsx.
 */
export const MyBidsPictureInPictureProvider = ({
  children,
}: {
  children?: ReactNode;
}) => {
  const {
    flags: { myBidsPictureInPicture },
  } = useFeatureFlags();
  const { supported, pipWindow, open, close } = useDocumentPictureInPicture({
    width: 380,
    height: 460,
  });
  const enabled = supported && !!myBidsPictureInPicture;

  // Take the window down if the flag is turned off mid-session.
  useEffect(() => {
    if (!enabled && pipWindow) close();
  }, [enabled, pipWindow, close]);

  return (
    <MyBidsPictureInPictureContextProvider
      value={{ supported: enabled, open: !!pipWindow, requestOpen: open }}
    >
      {children}
      {enabled && pipWindow ? (
        <Suspense fallback={null}>
          <MyBidsPictureInPictureWindow pipWindow={pipWindow} />
        </Suspense>
      ) : null}
    </MyBidsPictureInPictureContextProvider>
  );
};
